// Response schemas for the Gantt, milestones, people and capacity endpoints (plan §4, spec F9, F10).
// Built on the shared status list so web and (phase 2) mobile read the same contract.
import { TASK_STATUSES } from '@cis/shared';
import { z } from 'zod';

// Calendar date: matches "2026-11-04"; rejects "2026-11-04T00:00Z", "4 Nov".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B4%7D-%5Cd%7B2%7D-%5Cd%7B2%7D%24&testString=2026-11-04%0A2026-11-04T00%3A00Z%0A4%20Nov
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableDay = day.nullable();

const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  });

export const ganttBarSchema = z.object({
  taskId: z.string(),
  title: z.string(),
  phase: z.string(),
  area: z.string(),
  lane: z.string(),
  tier: z.string(),
  status: z.enum(TASK_STATUSES),
  percent: z.number(),
  start: nullableDay,
  end: nullableDay,
  plannedStart: nullableDay,
  plannedEnd: nullableDay,
  replanned: z.boolean(),
  overdue: z.boolean(),
  retired: z.boolean(),
  assignees: z.array(z.object({ id: z.number().int(), displayName: z.string() })),
});

export const phaseBandSchema = z.object({
  code: z.string(),
  title: z.string(),
  start: nullableDay,
  end: nullableDay,
});

export const ganttPageSchema = pageOf(ganttBarSchema).extend({
  today: day,
  range: z.object({ from: day, to: day }).nullable(),
  phases: z.array(phaseBandSchema),
});

export const milestoneSchema = z.object({
  code: z.string(),
  title: z.string(),
  phase: z.string().nullable(),
  dueOn: day,
  type: z.string().nullable(),
  past: z.boolean(),
});
export const milestonePageSchema = pageOf(milestoneSchema);

export const planningPersonSchema = z.object({
  id: z.number().int(),
  displayName: z.string(),
  role: z.string(),
  site: z.string().nullable(),
  active: z.boolean(),
});

export const personItemSchema = planningPersonSchema.extend({
  openTasks: z.number().int(),
  thisWeekHours: z.number(),
});
export const peoplePageSchema = pageOf(personItemSchema);

export const weekLoadSchema = z.object({
  weekStart: day,
  hours: z.number(),
  tasks: z.array(z.object({ taskId: z.string(), title: z.string(), hours: z.number() })),
});

export const personLoadSchema = z.object({
  person: planningPersonSchema,
  today: day,
  weeks: z.array(weekLoadSchema),
});

export const capacitySchema = z.object({
  packVersion: z.string().nullable(),
  capacity: z
    .object({
      headline: z.string(),
      weekHours: z.number(),
      weekly: z.array(
        z.object({
          week: z.string(),
          start: day,
          core: z.number(),
          target: z.number(),
          stretch: z.number(),
          post: z.number(),
          itCoreTarget: z.number(),
        }),
      ),
    })
    .nullable(),
});

export type GanttBar = z.infer<typeof ganttBarSchema>;
export type PhaseBand = z.infer<typeof phaseBandSchema>;
export type GanttPage = z.infer<typeof ganttPageSchema>;
export type Milestone = z.infer<typeof milestoneSchema>;
export type MilestonePage = z.infer<typeof milestonePageSchema>;
export type PersonItem = z.infer<typeof personItemSchema>;
export type PeoplePage = z.infer<typeof peoplePageSchema>;
export type WeekLoad = z.infer<typeof weekLoadSchema>;
export type PersonLoad = z.infer<typeof personLoadSchema>;
export type CapacityView = z.infer<typeof capacitySchema>;
